import React, {useEffect, useState, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../core'
import {KTCard, KTCardHeader, KTCardBody, ChartColumn, Select} from '../../components'
import {Link} from 'react-router-dom'

import {useDispatch, useSelector} from 'react-redux'
import {bankCashDashboard, generalDashboard, getCurrencyInfos} from '../../redux/slices/common.slice'
import {calendarGetAll} from '../../redux/slices/calendar.slice'
import {taskCalendarGetAll} from '../../redux/slices/task-calendar.slice'

import {KTSVG, detailPageUrl, DetailPage, ExpenseDetailPage, AuthRole} from '../../helpers'

import FullCalendar from '@fullcalendar/react'
import listPlugin from '@fullcalendar/list'
import trLocale from '@fullcalendar/core/locales/tr'

const SOURCE_TYPE_COLORS = {
  27: '#EF4444',
  28: '#8B5CF6',
  9:  '#F97316',
  12: '#F59E0B',
  29: '#84CC16',
  33: '#3B82F6',
}

const TASK_COLOR = '#14B8A6'

const selectProfileInfo = (state) => state.profile.info
const selectCommonDashboardData = (state) => ({
  bankCashDashResult: state.common.bankCashDashResult,
  generalDashResult: state.common.generalDashResult,
  currencyInfos: state.common.currencyInfos,
  loading: state.common.loading,
  error: state.common.error,
})
const selectCalendarData = (state) => state.calendar.calendar
const selectWorkCalendarData = (state) => state.taskCalendar.calendar

function Dashboard() {
  const intl = useIntl()
  const dispatch = useDispatch()

  const info = useSelector(selectProfileInfo)
  const {bankCashDashResult, generalDashResult, currencyInfos, loading, error} =
    useSelector(selectCommonDashboardData)
  const calendarData = useSelector(selectCalendarData)
  const workCalendarData = useSelector(selectWorkCalendarData)

  const [salesProjectId, setSalesProjectId] = useState(null)
  const [expensesProjectId, setExpensesProjectId] = useState(null)

  const hasBankRole = useMemo(() => {
    return info?.roles?.includes(AuthRole.Bank.name) ?? false
  }, [info?.roles])

  const hasTaskRole = useMemo(() => {
    return info?.roles?.includes(AuthRole.Task.name) ?? false
  }, [info?.roles])

  const calendarDates = useMemo(() => {
    const now = new Date()
    return {
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    }
  }, [])

  const calendarEvents = useMemo(() => {
    if (!calendarData) return []
    return calendarData.map((event) => ({
      id: event.id,
      title: event.sourceTypeName + ' - ' + event.title,
      date: (() => { const d = new Date(event.startDate); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` })(),
      url: event.url,
      backgroundColor: SOURCE_TYPE_COLORS[event.sourceTypeId] ?? '#3B82F6',
      borderColor: SOURCE_TYPE_COLORS[event.sourceTypeId] ?? '#3B82F6',
    }))
  }, [calendarData])

  const workCalendarEvents = useMemo(() => {
    if (!workCalendarData) return []
    return workCalendarData.map((event) => ({
      id: event.id,
      title: event.sourceTypeName + ' - ' + event.title,
      date: (() => { const d = new Date(event.startDate); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` })(),
      url: event.url,
      backgroundColor: TASK_COLOR,
      borderColor: TASK_COLOR,
    }))
  }, [workCalendarData])

  const handleSalesProjectChange = useCallback((item) => {
    setSalesProjectId(item?.value)
  }, [])

  const handleExpensesProjectChange = useCallback((item) => {
    setExpensesProjectId(item?.value)
  }, [])

  useEffect(() => {
    if (hasBankRole) {
      dispatch(bankCashDashboard({}))
    }
    dispatch(generalDashboard({}))
    dispatch(calendarGetAll(calendarDates))
    dispatch(getCurrencyInfos({}))
    if (hasTaskRole) {
      dispatch(taskCalendarGetAll(calendarDates))
    }
  }, [dispatch, hasBankRole, hasTaskRole, calendarDates])

  const loadingComponent = useMemo(
    () => (
      <div className='page-loader flex-column bg-dark bg-opacity-50'>
        <span className='spinner-border text-primary' role='status'></span>
        <span className='text-light fs-6 fw-semibold mt-5'>
          {intl.formatMessage({id: 'LOADING'})}
        </span>
      </div>
    ),
    [intl]
  )

  const errorComponent = useMemo(
    () => (
      <div className='alert alert-danger'>
        {error?.message || intl.formatMessage({id: 'ERROR'})}
      </div>
    ),
    [error?.message, intl]
  )

  if (loading) {
    return loadingComponent
  }

  if (error) {
    return errorComponent
  }

  const Widget = ({
    className,
    color,
    svgIcon,
    iconColor,
    title,
    titleColor,
    description,
    descriptionColor,
    link,
  }) => {
    return (
      <div className={`card bg-${color} ${className}`}>
        <div className='card-body'>
          <KTSVG
            path={svgIcon}
            className={`svg-icon-${iconColor} svg-icon-5x ms-n1`}
            svgClassName={'mh-100px'}
          />

          <div className={`text-${titleColor} fw-bold fs-1 mb-2 mt-5`}>{title}</div>

          {link ? (
            <Link to={link} className={`fw-semibold text-${descriptionColor}`}>
              {description}
            </Link>
          ) : (
            <div className={`fw-semibold text-${descriptionColor}`}>{description}</div>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <PageTitle>{`${intl.formatMessage({id: 'DASHBOARD.HELLO'})}, ${
        info ? info.firstName : ''
      }`}</PageTitle>
      <div className='row mb-10'>
        <div className='col-xl-2'>
          <Widget
            className='card-xl-stretch'
            svgIcon='/images/icons/duotune/general/gen019.svg'
            color='danger'
            iconColor='white'
            title={
              generalDashResult?.totalUserTaskCount
                ? `${generalDashResult?.totalUserTaskCount} ${intl.formatMessage({
                    id: 'DASHBOARD.TOTALUSERTASK.TASK',
                  })}`
                : intl.formatMessage({id: 'DASHBOARD.TOTALUSERTASK.NONTASK'})
            }
            description={intl.formatMessage({id: 'DASHBOARD.TOTALUSERTASK.LINK'})}
            titleColor='white'
            descriptionColor='white'
            link={'/user/task'}
          />
        </div>        

        {currencyInfos && currencyInfos.length > 0 && (
          <div className='col-xl-3'>
            <Widget
              className='card-xl-stretch'
              svgIcon='/images/icons/duotune/finance/fin003.svg'
              color='warning'
              iconColor='white'
              title={
                <div className='row g-0'>
                  {currencyInfos.map((item, index) => (
                    <div className='col-6 fs-4 mb-2' key={index}>
                      {intl.formatNumber(item.currencyRate, {
                        style: 'currency',
                        currency: item.currencyCode,
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  ))}
                </div>
              }
              titleColor='white'
              descriptionColor='white'
            />
          </div>
        )}

        {hasBankRole && (
          <>
            <div className='col-xl-2'>
              <Widget
                className='card-xl-stretch'
                svgIcon='/images/icons/duotune/finance/fin008.svg'
                color='primary'
                iconColor='white'
                title={intl.formatNumber(
                  bankCashDashResult?.cashFlows?.totalDefaultCurrencyAmount,
                  {style: 'currency', currency: 'try', minimumFractionDigits: 0, maximumFractionDigits: 0}
                )}
                description={intl.formatMessage({id: 'DASHBOARD.TOTALDEFAULTCURRENCYAMOUNT'})}
                titleColor='white'
                descriptionColor='white'
              />
            </div>
            <div className='col-xl-2'>
              <Widget
                className='card-xl-stretch'
                svgIcon='/images/icons/duotune/finance/fin010.svg'
                color='info'
                iconColor='white'
                title={intl.formatNumber(
                  bankCashDashResult?.cashFlows?.totalOtherCurrenciesAmount,
                  {style: 'currency', currency: 'try', minimumFractionDigits: 0, maximumFractionDigits: 0}
                )}
                description={intl.formatMessage({id: 'DASHBOARD.TOTALOTHERCURRENCYAMOUNT'})}
                titleColor='white'
                descriptionColor='white'
              />
            </div>
            <div className='col-xl-3'>
              <Widget
                className='card-xl-stretch'
                svgIcon='/images/icons/duotune/finance/fin001.svg'
                color='success'
                iconColor='white'
                title={intl.formatNumber(bankCashDashResult?.cashFlows?.totalAllCurrenciesAmount, {
                  style: 'currency',
                  currency: 'try', minimumFractionDigits: 0, maximumFractionDigits: 0,
                })}
                description={intl.formatMessage({id: 'DASHBOARD.TOTALALLCURRENCYAMOUNT'})}
                titleColor='white'
                descriptionColor='white'
              />
            </div>
          </>
        )}
      </div>

      <div className='row mb-10'>
        <div className={hasTaskRole ? 'col-6' : 'col-12'}>
          <KTCard>
            <KTCardHeader title={intl.formatMessage({id: 'MENU.CALENDAR'})}>
              <div className='card-toolbar'>
                <Link to='/calendar' className='btn btn-sm btn-light-primary'>
                  {intl.formatMessage({id: 'BUTTON.DETAIL'})}
                </Link>
              </div>
            </KTCardHeader>
            <KTCardBody>
              <FullCalendar
                plugins={[listPlugin]}
                initialView='listMonth'
                locale={trLocale}
                headerToolbar={false}
                height={400}
                events={calendarEvents}
                eventClick={(info) => {
                  info.jsEvent.preventDefault()
                  if (info.event.url) window.open(info.event.url, '_blank')
                }}
              />
            </KTCardBody>
          </KTCard>
        </div>
        {hasTaskRole && (
          <div className='col-6'>
            <KTCard>
              <KTCardHeader title={intl.formatMessage({id: 'MENU.WORKPLAN.WORKCALENDAR'})}>
                <div className='card-toolbar'>
                  <Link to='/workplan/work-calendar' className='btn btn-sm btn-light-primary'>
                    {intl.formatMessage({id: 'BUTTON.DETAIL'})}
                  </Link>
                </div>
              </KTCardHeader>
              <KTCardBody>
                <FullCalendar
                  plugins={[listPlugin]}
                  initialView='listMonth'
                  locale={trLocale}
                  headerToolbar={false}
                  height={400}
                  events={workCalendarEvents}
                  eventClick={(info) => {
                    info.jsEvent.preventDefault()
                    if (info.event.url) window.open(info.event.url, '_blank')
                  }}
                />
              </KTCardBody>
            </KTCard>
          </div>
        )}
      </div>

      <div className='row gy-5 g-xl-10'>
        {hasBankRole && bankCashDashResult?.cashFlows?.paymentPlansMonthlyExpenses && (
          <>
            <div className='col-xl-8 mb-10'>
              <ChartColumn
                chartHeight='500px'
                categories={bankCashDashResult?.cashFlows?.paymentPlansMonthlyExpenses?.map(
                  (item) => `${item?.monthName}`
                )}
                data={[
                  {
                    name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANEXPENSES'}),
                    data: bankCashDashResult?.cashFlows?.paymentPlansMonthlyExpenses?.map(
                      (item) => item?.totalPrice
                    ),
                  },
                  {
                    name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSALES'}),
                    data: bankCashDashResult?.cashFlows?.paymentPlansMonthlySales?.map(
                      (item) => item?.totalPrice
                    ),
                  },
                  {
                    name: intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSCASHFLOW'}),
                    data: bankCashDashResult?.cashFlows?.paymentPlansCashFlows?.map(
                      (item) => item?.cashFlow
                    ),
                  },
                ]}
                isMultipleColumn={true}
                title={intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSCASHFLOW'})}
                tooltip={{
                  y: {
                    formatter: (val) => {
                      return intl.formatNumber(val, {style: 'currency', currency: 'try', minimumFractionDigits: 2, maximumFractionDigits: 4})
                    },
                  },
                }}
              />
            </div>
            <div className='col-xl-4 mb-10'>
              <div className='row gy-5 g-xl-10'>
                <div className='col-xl-12'>
                  <KTCard>
                    <KTCardHeader
                      title={intl.formatMessage({
                        id: 'DASHBOARD.CASHFLOW.CURRENTBANKCASHACCOUNTS',
                      })}
                    />
                    <KTCardBody>
                      <div className='table-responsive'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                          <thead>
                            <tr className='fw-bold text-muted'>
                              <th className='min-w-150px'>
                                {intl.formatMessage({id: 'DASHBOARD.CASHFLOW.ACCOUNTNAME'})}
                              </th>
                              <th className='min-w-140px'>
                                {intl.formatMessage({id: 'DASHBOARD.CASHFLOW.TOTALPRICE'})}
                              </th>
                              <th className='min-w-140px'>
                                {intl.formatMessage({id: 'DASHBOARD.CASHFLOW.DEFAULTTOTALPRICE'})}
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {bankCashDashResult?.cashFlows?.currenctBankCashAccounts?.map(
                              (item, index) => {
                                return (
                                  <tr key={index}>
                                    <td>
                                      <div className='d-flex align-items-center'>
                                        <div className='d-flex justify-content-start flex-column'>
                                          {item.name}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <span className='text-dark fw-bold d-block fs-6'>
                                        {intl.formatNumber(item?.totalAmount, {
                                          style: 'currency',
                                          currency: item?.currencyCode,
                                        })}
                                        {item?.currentRate != 0 && (
                                          <>
                                            <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                              {intl.formatNumber(item?.currentRate, {
                                                style: 'currency',
                                                currency: 'try',
                                              })}
                                            </span>
                                          </>
                                        )}
                                      </span>
                                    </td>
                                    <td>
                                      <span className='text-dark fw-bold d-block fs-6'>
                                        {intl.formatNumber(item?.defaultCurrenctAmount, {
                                          style: 'currency',
                                          currency: 'try',
                                        })}
                                      </span>
                                    </td>
                                  </tr>
                                )
                              }
                            )}
                          </tbody>
                        </table>
                      </div>
                    </KTCardBody>
                  </KTCard>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {hasBankRole && (
        <>
          <div className='row gy-5 g-xl-10 mb-10'>
            <div className='col-xl-6'>
              <KTCard>
                <KTCardHeader title={intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSEXPENSES'})}>
                  <div className='card-toolbar'>
                    <Select
                      options={[
                        {label: intl.formatMessage({id: 'FILTER.ALL'}), value: null},
                        ...Array.from(
                          new Set(
                            bankCashDashResult?.paymentPlansExpenses?.map((item) => item.projectId)
                          )
                        ).map((projectId) => {
                          const project = bankCashDashResult?.paymentPlansExpenses?.find(
                            (item) => item.projectId === projectId
                          )
                          return {
                            label: project.projectName,
                            value: projectId,
                          }
                        }),
                      ]}
                      onChange={handleExpensesProjectChange}
                      isLoading={false}
                    />
                  </div>
                </KTCardHeader>
                <KTCardBody>
                  <div className='card-scroll mh-400px'>
                    <div className='table-responsive'>
                      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        <thead>
                          <tr className='fw-bold text-muted'>
                            <th className='min-w-150px'>
                              {intl.formatMessage({
                                id: 'DASHBOARD.PAYMENTPLANSEXPENSES.PROJECTNAME',
                              })}
                            </th>
                            <th className='min-w-150px'>
                              {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSEXPENSES.NAME'})}
                            </th>
                            <th className='min-w-150px'>
                              {intl.formatMessage({
                                id: 'DASHBOARD.PAYMENTPLANSEXPENSES.PAYMENTPLANTYPENAME',
                              })}{' '}
                              -{' '}
                              {intl.formatMessage({
                                id: 'DASHBOARD.PAYMENTPLANSEXPENSES.COMPANYNAME',
                              })}
                            </th>
                            <th className='min-w-140px'>
                              {intl.formatMessage({
                                id: 'DASHBOARD.PAYMENTPLANSEXPENSES.DEFERMENTDATE',
                              })}
                            </th>
                            <th className='min-w-120px'>
                              {intl.formatMessage({
                                id: 'DASHBOARD.PAYMENTPLANSEXPENSES.TOTALPRICE',
                              })}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {bankCashDashResult?.paymentPlansExpenses
                            ?.filter(
                              (item) => !expensesProjectId || item.projectId === expensesProjectId
                            )
                            ?.map((item, index) => {
                              return (
                                <tr key={index}>
                                  <td>
                                    <div className='d-flex align-items-center'>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <Link
                                          to={detailPageUrl(DetailPage.Project, item.projectId)}
                                          className='text-dark fw-bold fs-6 text-hover-primary'
                                          target='_blank'
                                        >
                                          {item?.projectName}
                                        </Link>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className='d-flex align-items-center'>
                                      <div className='d-flex justify-content-start flex-column'>
                                        {item?.expenseInvoiceId && (
                                          <>
                                            <Link
                                              to={detailPageUrl(
                                                ExpenseDetailPage[item.expenseTypeId],
                                                item.expenseInvoiceId
                                              )}
                                              className='text-dark fw-bold fs-6 text-hover-primary'
                                              target='_blank'
                                            >
                                              {item.description}
                                            </Link>
                                          </>
                                        )}
                                        {item?.salaryId && (
                                          <>
                                            <Link
                                              to={detailPageUrl(DetailPage.Salary, item.salaryId)}
                                              className='text-dark fw-bold fs-6 text-hover-primary'
                                              target='_blank'
                                            >
                                              {item.description}
                                            </Link>
                                          </>
                                        )}
                                        {item?.checkId && (
                                          <>
                                            <Link
                                              to={detailPageUrl(DetailPage.Check, item.checkId)}
                                              className='text-dark fw-bold fs-6 text-hover-primary'
                                              target='_blank'
                                            >
                                              {item.description}
                                            </Link>
                                          </>
                                        )}
                                        <div>
                                          {item?.expenseInvoiceId && (
                                            <>
                                              <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                                <Link
                                                  to={detailPageUrl(
                                                    ExpenseDetailPage[item.expenseTypeId],
                                                    item.expenseInvoiceId
                                                  )}
                                                  className='text-dark fs-6 text-hover-primary'
                                                  target='_blank'
                                                >
                                                  {item.name}
                                                </Link>
                                              </span>
                                            </>
                                          )}
                                          {item?.salaryId && (
                                            <>
                                              <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                                <Link
                                                  to={detailPageUrl(
                                                    DetailPage.Salary,
                                                    item.salaryId
                                                  )}
                                                  className='text-dark fs-6 text-hover-primary'
                                                  target='_blank'
                                                >
                                                  {item.name}
                                                </Link>
                                              </span>
                                            </>
                                          )}
                                          {item?.checkId && (
                                            <>
                                              <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                                <Link
                                                  to={detailPageUrl(DetailPage.Check, item.checkId)}
                                                  className='text-dark fs-6 text-hover-primary'
                                                  target='_blank'
                                                >
                                                  {item.name}
                                                </Link>
                                              </span>
                                            </>
                                          )}
                                          {item?.expenseInvoiceId == null &&
                                            item?.salaryId == null &&
                                            item?.checkId == null && (
                                              <>
                                                <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                                  {item.name}
                                                </span>
                                              </>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span className='text-dark fw-bold d-block fs-6'>
                                      {item?.paymentPlanTypeName}
                                      {item?.companyName && (
                                        <>
                                          <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                            {item?.companyName}
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  </td>
                                  <td>
                                    <span className='text-dark fw-bold d-block fs-6'>
                                      {intl.formatDate(new Date(item?.defermenDate), {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                      })}
                                    </span>
                                  </td>
                                  <td>
                                    <span className='text-dark fw-bold d-block fs-6'>
                                      {intl.formatNumber(item?.totalPrice, {
                                        style: 'currency',
                                        currency: 'try',
                                      })}
                                    </span>
                                  </td>
                                </tr>
                              )
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>

            <div className='col-xl-6'>
              <KTCard>
                <KTCardHeader title={intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES'})}>
                  <div className='card-toolbar'>
                    <Select
                      options={[
                        {label: intl.formatMessage({id: 'FILTER.ALL'}), value: null},
                        ...Array.from(
                          new Set(
                            bankCashDashResult?.paymentPlansSales?.map((item) => item.projectId)
                          )
                        ).map((projectId) => {
                          const project = bankCashDashResult?.paymentPlansSales?.find(
                            (item) => item.projectId === projectId
                          )
                          return {
                            label: project.projectName,
                            value: projectId,
                          }
                        }),
                      ]}
                      onChange={handleSalesProjectChange}
                      isLoading={false}
                    />
                  </div>
                </KTCardHeader>
                <KTCardBody>
                  <div className='card-scroll mh-400px'>
                    <div className='table-responsive'>
                      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        <thead>
                          <tr className='fw-bold text-muted'>
                            <th className='min-w-150px'>
                              {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES.PROJECTNAME'})}
                            </th>
                            <th className='min-w-150px'>
                              {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES.NAME'})}
                            </th>
                            <th className='min-w-150px'>
                              {intl.formatMessage({
                                id: 'DASHBOARD.PAYMENTPLANSEXPENSES.PAYMENTPLANTYPENAME',
                              })}{' '}
                              -{' '}
                              {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES.COMPANYNAME'})}
                            </th>
                            <th className='min-w-140px'>
                              {intl.formatMessage({
                                id: 'DASHBOARD.PAYMENTPLANSSALES.DEFERMENTDATE',
                              })}
                            </th>
                            <th className='min-w-120px'>
                              {intl.formatMessage({id: 'DASHBOARD.PAYMENTPLANSSALES.TOTALPRICE'})}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {bankCashDashResult?.paymentPlansSales
                            ?.filter((item) => !salesProjectId || item.projectId === salesProjectId)
                            ?.map((item, index) => {
                              return (
                                <tr key={index}>
                                  <td>
                                    <div className='d-flex align-items-center'>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <Link
                                          to={detailPageUrl(DetailPage.Project, item.projectId)}
                                          className='text-dark fw-bold fs-6 text-hover-primary'
                                          target='_blank'
                                        >
                                          {item?.projectName}
                                        </Link>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className='d-flex align-items-center'>
                                      <div className='d-flex justify-content-start flex-column'>
                                        {item?.saleInvoiceId && (
                                          <>
                                            <Link
                                              to={detailPageUrl(
                                                DetailPage.SaleInvoice,
                                                item.saleInvoiceId
                                              )}
                                              className='text-dark fw-bold fs-6 text-hover-primary'
                                              target='_blank'
                                            >
                                              {item.description}
                                            </Link>
                                          </>
                                        )}
                                        {item?.checkId && (
                                          <>
                                            <Link
                                              to={detailPageUrl(DetailPage.Check, item.checkId)}
                                              className='text-dark fw-bold fs-6 text-hover-primary'
                                              target='_blank'
                                            >
                                              {item.description}
                                            </Link>
                                          </>
                                        )}
                                        <div>
                                          {item?.saleInvoiceId && (
                                            <>
                                              <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                                <Link
                                                  to={detailPageUrl(
                                                    DetailPage.SaleInvoice,
                                                    item.saleInvoiceId
                                                  )}
                                                  className='text-dark fs-6 text-hover-primary'
                                                  target='_blank'
                                                >
                                                  {item.name}
                                                </Link>
                                              </span>
                                            </>
                                          )}
                                          {item?.checkId && (
                                            <>
                                              <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                                <Link
                                                  to={detailPageUrl(DetailPage.Check, item.checkId)}
                                                  className='text-dark fs-6 text-hover-primary'
                                                  target='_blank'
                                                >
                                                  {item.name}
                                                </Link>
                                              </span>
                                            </>
                                          )}
                                          {item?.saleInvoiceId == null && item?.checkId == null && (
                                            <>
                                              <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                                {item.name}
                                              </span>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span className='text-dark fw-bold d-block fs-6'>
                                      {item?.paymentPlanTypeName}
                                      {item?.companyName && (
                                        <>
                                          <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                            {item?.companyName}
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  </td>
                                  <td>
                                    <span className='text-dark fw-bold d-block fs-6'>
                                      {intl.formatDate(new Date(item?.defermenDate), {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                      })}
                                    </span>
                                  </td>
                                  <td>
                                    <span className='text-dark fw-bold d-block fs-6'>
                                      {intl.formatNumber(item?.totalPrice, {
                                        style: 'currency',
                                        currency: 'try',
                                      })}
                                    </span>
                                  </td>
                                </tr>
                              )
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>
          </div>
        </>
      )}
      <div className='row mt-6'>
        <div className='col-12'>
          <div className='card card-flush bg-light-primary border-0'>
            <div className='card-body py-5 d-flex align-items-center gap-5'>
              <KTSVG
                path='/images/icons/nav/Help.svg'
                className='svg-icon-primary svg-icon-3x flex-shrink-0'
              />
              <div className='flex-grow-1'>
                <div className='fw-bold text-dark fs-5'>Sistemi kullanmakta yardıma mı ihtiyacınız var?</div>
                <div className='text-muted fs-7'>Adım adım kılavuzlar ve ipuçları için Yardım Merkezi&apos;ni ziyaret edin.</div>
              </div>
              <Link to='/help' className='btn btn-primary btn-sm flex-shrink-0'>
                Yardım Merkezi
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
export {Dashboard}
